import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Ban, FileText, LoaderCircle, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import InvoicePdfButton, { runInvoicePdfAction } from "./InvoicePdfButton";
import {
  IDispatchHistory,
  IOrder,
  IOrderAddress,
  IOrderGoods,
} from "../../models/Order.model";
import { RootDispatch, RootState } from "../../store";
import {
  DistanceResult,
  cancelEInvoice,
  generateIRN,
  getDistance,
  getDispatchDetails,
  getGoodsForInvoice,
  getInvoiceById,
  getNextEInvoiceNumber,
} from "../../store/slices/einvoiceSlice";
import {
  fetchWarehouseList,
  fetchWarehouseNamesAndIdsThunk,
} from "../../store/slices/warehouseSlice";
import InvoiceAddressFields from "./InvoiceAddressFields";
import InvoiceSummary from "./InvoiceSummary";
import {
  EwbDtls,
  InvoiceData,
} from "../../models/invoice/invoice.model";
import ResponseModel from "../../models/response.model";
import ConfirmationModal from "../ConfirmationModalComponent/ConfirmationModal";
import {
  isValidIndianVehicleNumber,
  normalizeIndianVehicleNumber,
} from "../../utils/indianVehicleNumber";

type EwbFormState = Record<Exclude<keyof EwbDtls, "TransDocNo">, string>;

type EwbGenerationError = {
  invoiceId: string;
  invoiceNumber: string;
  reason: string;
};

const getEwbGenerationErrorReason = (invoice: InvoiceData) =>
  invoice.taxProResponse?.InfoDtls?.filter(
    (info) => info.InfCd === "EWBERR",
  )
    .flatMap((info) => info.Desc?.map((error) => error.ErrorMessage) || [])
    .filter((message): message is string => Boolean(message?.trim()))
    .join(" ");

const getLatestDispatchHistory = (history: IDispatchHistory[] = []) =>
  history.reduce<IDispatchHistory | undefined>((latest, current) => {
    if (!latest) return current;
    const currentTime = new Date(current.dispatchDate).getTime();
    const latestTime = new Date(latest.dispatchDate).getTime();
    if (!Number.isFinite(latestTime)) return current;
    return Number.isFinite(currentTime) && currentTime > latestTime
      ? current
      : latest;
  }, undefined);

const toEwbTransportMode = (
  transportMode?: string,
): EwbDtls["TransMode"] | "" => {
  switch (transportMode?.trim().toLowerCase()) {
    case "1":
    case "road":
    case "courier":
    case "self pick-up":
      return "1";
    case "2":
    case "rail":
      return "2";
    case "3":
    case "air":
      return "3";
    case "4":
    case "sea":
    case "ship":
      return "4";
    default:
      return "";
  }
};

const toDateInputValue = (value?: Date | string) => {
  if (!value) return "";
  if (typeof value === "string") {
    const dateOnly = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (dateOnly) return dateOnly;
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getEwbFormFromDispatch = (history?: IDispatchHistory): EwbFormState => {
  const transporterId = history?.transporterId || "";

  return {
    TransId: transporterId,
    TransName: transporterId ? "" : history?.transporterName || "",
    Distance: history?.distance === undefined ? "" : String(history.distance),
    TransDocDt: toDateInputValue(new Date()),
    VehNo: history?.vehicleNumber || "",
    VehType: history?.vehicleType || "R",
    TransMode: toEwbTransportMode(history?.transportMode) || "1",
  };
};

const getAddressForm = (address?: IOrderAddress): IOrderAddress => ({
  line1: address?.line1 || "",
  line2: address?.line2 || address?.address || "",
  address: address?.address || "",
  city: address?.city || "",
  state: address?.state || "",
  pincode: address?.pincode || "",
});

const getInvoiceGoodsTotal = (goods: IOrderGoods[]) =>
  goods.reduce((total, good) => {
    const quantity = Number(good.quantity) || 0;
    const price = Number(good.price) || 0;
    const discount = Number(good.discount) || 0;
    const gst = Number(good.gst || good.product?.gst) || 0;
    const taxableAmount = price * quantity - discount;
    return total + taxableAmount * (1 + gst / 100);
  }, 0);

type InvoiceActionsProps = {
  order?: IOrder;
  type: "order" | "epc";
  className?: string;
  buttonClassName?: string;
  onGenerateInvoice?: (
    orderId: string,
    invoiceNumber: string,
    ewbDetails?: EwbDtls,
    warehouseId?: string,
    billingAddress?: IOrderAddress,
    shippingAddress?: IOrderAddress,
    goodsIds?: string[],
  ) => ResponseModel | void | Promise<ResponseModel | void>;
  onInvoiceGenerated?: () => void;
};

const InvoiceActions: React.FC<InvoiceActionsProps> = ({
  order,
  type,
  className = "relative",
  buttonClassName = "inline-flex min-h-9 items-center gap-2 whitespace-nowrap rounded-md border border-emerald-800 bg-emerald-800 px-3 text-xs font-semibold text-white shadow-sm transition hover:border-emerald-900 hover:bg-emerald-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 active:translate-y-px",
  onGenerateInvoice,
  onInvoiceGenerated,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const warehouseNamesAndIds = useSelector(
    (state: RootState) => state.warehouse.warehouseNamesAndIds,
  );
  const warehouses = useSelector(
    (state: RootState) => state.warehouse.warehouses,
  );
  const distanceLoading = useSelector(
    (state: RootState) => state.einvoice.loading.getDistance,
  );
  const goodsLoading = useSelector(
    (state: RootState) => state.einvoice.loading.getGoodsForInvoice,
  );
  const goodsForInvoice = useSelector(
    (state: RootState) => state.einvoice.goodsForInvoice,
  );
  const cancelInvoiceLoading = useSelector(
    (state: RootState) => state.einvoice.loading.cancelEInvoice,
  );

  const invoiceRecords = order?.invoice?.invoiceId ?? [];
  const [showInvoiceMenu, setShowInvoiceMenu] = useState(false);
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelInvoiceError, setCancelInvoiceError] = useState("");
  const [showEwbForm, setShowEwbForm] = useState(false);
  const [showThresholdConfirmation, setShowThresholdConfirmation] =
    useState(false);
  const [showInvoiceSummary, setShowInvoiceSummary] = useState(false);
  const [showGoodsSelection, setShowGoodsSelection] = useState(false);
  const [ewbGenerationErrors, setEwbGenerationErrors] = useState<
    EwbGenerationError[]
  >([]);
  const [ewbStatusLoading, setEwbStatusLoading] = useState(false);
  const [ewbStatusLoadError, setEwbStatusLoadError] = useState(false);
  const dispatchDetailsLoadedForOrder = useRef<string | null>(null);
  const [selectedGoodsIds, setSelectedGoodsIds] = useState<string[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(false);
  const [pendingEwbDetails, setPendingEwbDetails] = useState<EwbDtls>();
  const [ewbForm, setEwbForm] = useState<EwbFormState>(() =>
    getEwbFormFromDispatch(getLatestDispatchHistory(order?.dispatchHistory)),
  );
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [billingAddress, setBillingAddress] = useState<IOrderAddress>(() =>
    getAddressForm(order?.billingAddress),
  );
  const [shippingAddress, setShippingAddress] = useState<IOrderAddress>(() =>
    getAddressForm(order?.shippingAddress),
  );

  useEffect(() => {
    if (!showInvoiceMenu || type !== "order") return;

    const invoicesToCheck = (order?.invoice?.invoiceId ?? []).filter(
      (invoice) => invoice._id,
    );
    if (invoicesToCheck.length === 0) {
      setEwbGenerationErrors([]);
      setEwbStatusLoadError(false);
      setEwbStatusLoading(false);
      return;
    }

    let cancelled = false;
    setEwbGenerationErrors([]);
    setEwbStatusLoadError(false);
    setEwbStatusLoading(true);

    const loadEwbErrors = async () => {
      const results = await Promise.all(
        invoicesToCheck.map(async (invoice) => {
          try {
            const response = await dispatch(
              getInvoiceById({ invoiceId: invoice._id }),
            ).unwrap();
            if (response.success === false || !response.data) {
              throw new Error(response.message || "Unable to load invoice.");
            }

            const reason = getEwbGenerationErrorReason(
              response.data as InvoiceData,
            );
            return {
              fetchFailed: false,
              error: reason
                ? {
                    invoiceId: invoice._id,
                    invoiceNumber: invoice.invoiceNumber,
                    reason,
                  }
                : null,
            };
          } catch (error) {
            console.error(
              `Failed to fetch EWB status for invoice ${invoice._id}:`,
              error,
            );
            return { fetchFailed: true, error: null };
          }
        }),
      );

      if (cancelled) return;
      setEwbGenerationErrors(
        results.flatMap((result) => (result.error ? [result.error] : [])),
      );
      setEwbStatusLoadError(results.some((result) => result.fetchFailed));
      setEwbStatusLoading(false);
    };

    void loadEwbErrors();
    return () => {
      cancelled = true;
    };
  }, [dispatch, order?.invoice?.invoiceId, showInvoiceMenu, type]);

  useEffect(() => {
    const destinationPincode = shippingAddress.pincode;
    if (!showEwbForm || !selectedWarehouseId || !destinationPincode) {
      return;
    }

    const originPincode = warehouses.find(
      (warehouse) => warehouse._id === selectedWarehouseId,
    )?.pincode;
    if (!originPincode) return;

    let cancelled = false;
    const loadDistance = async () => {
      try {
        const response = await dispatch(
          getDistance({
            originPin: originPincode,
            destinationPin: destinationPincode,
          }),
        ).unwrap();
        if (response && response.success) {
          const distanceKm = (response.data as DistanceResult | undefined)
            ?.distanceKm;
          if (
            !cancelled &&
            typeof distanceKm === "number" &&
            Number.isFinite(distanceKm) &&
            distanceKm > 0
          ) {
            setEwbForm((current) => ({
              ...current,
              Distance: String(distanceKm),
            }));
          }
        } else {
          throw new Error("Failed to fetch distance");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch EWB distance:", error);
        }
      }
    };

    void loadDistance();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    selectedWarehouseId,
    shippingAddress.pincode,
    showEwbForm,
    warehouses,
  ]);

  useEffect(() => {
    if (
      !showEwbForm ||
      !order?._id ||
      dispatchDetailsLoadedForOrder.current === order._id
    ) {
      return;
    }

    dispatchDetailsLoadedForOrder.current = order._id;
    let cancelled = false;
    const loadDispatchDetails = async () => {
      try {
        const response = await dispatch(
          getDispatchDetails({ orderId: order._id }),
        ).unwrap();
        if (response.success === false) {
          throw new Error(
            response.message || "Failed to get dispatch details.",
          );
        }

        const responseHistory:
          | IDispatchHistory
          | IDispatchHistory[]
          | undefined = response.data?.dispatchHistory;
        const dispatchHistory = Array.isArray(responseHistory)
          ? getLatestDispatchHistory(responseHistory)
          : responseHistory;

        if (!dispatchHistory || cancelled) return;

        const transporterName = dispatchHistory.transporterName?.trim();
        const transporterId = dispatchHistory.transporterId?.trim();
        const transportMode = toEwbTransportMode(
          dispatchHistory.transportMode,
        );
        setEwbForm((current) => ({
          ...current,
          ...(transporterName
            ? { TransId: "", TransName: transporterName }
            : transporterId
              ? { TransId: transporterId, TransName: "" }
              : {}),
          ...(dispatchHistory.vehicleNumber
            ? { VehNo: dispatchHistory.vehicleNumber }
            : {}),
          ...(dispatchHistory.vehicleType
            ? { VehType: dispatchHistory.vehicleType }
            : {}),
          ...(transportMode ? { TransMode: transportMode } : {}),
        }));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch dispatch details for EWB:", error);
        }
      }
    };

    void loadDispatchDetails();
    return () => {
      cancelled = true;
    };
  }, [dispatch, order?._id, showEwbForm]);

  useEffect(() => {
    if (!showInvoiceMenu) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowInvoiceMenu(false);
        setShowEwbForm(false);
        setShowThresholdConfirmation(false);
        setShowInvoiceSummary(false);
        setShowGoodsSelection(false);
        setShowInvoiceList(false);
        setValidationError("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showInvoiceMenu]);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showInvoiceMenu) {
      setSelectedWarehouseId("");
      setBillingAddress(getAddressForm(order?.billingAddress));
      setShippingAddress(getAddressForm(order?.shippingAddress));
      if (warehouseNamesAndIds.length === 0) {
        dispatch(fetchWarehouseNamesAndIdsThunk());
      }
    }
    setShowEwbForm(false);
    setShowThresholdConfirmation(false);
    setShowInvoiceSummary(false);
    setShowGoodsSelection(false);
    setShowInvoiceList(false);
    setSelectedGoodsIds([]);
    setPendingEwbDetails(undefined);
    setValidationError("");
    setInvoiceNumber("");
    setShowInvoiceMenu((prev) => !prev);
  };

  const handleGenerateInvoice = () => {
    if (!order?._id) return;
    setValidationError("");
    void openGoodsSelection();
  };

  const updateEwbField = (field: keyof EwbFormState, value: string) => {
    setEwbForm((current) => ({ ...current, [field]: value }));
    setValidationError("");
  };

  const openGoodsSelection = async () => {
    if (!order?._id) return;
    dispatchDetailsLoadedForOrder.current = null;
    setPendingEwbDetails(undefined);
    setValidationError("");
    setSelectedGoodsIds([]);
    setShowInvoiceSummary(false);
    setShowEwbForm(false);
    setShowThresholdConfirmation(false);
    setShowGoodsSelection(true);
    setInvoiceNumber("");
    setInvoiceNumberLoading(true);
    void dispatch(getNextEInvoiceNumber())
      .unwrap()
      .then((response) => {
        if (response.success === false) {
          throw new Error(
            response.message || "Unable to load the next invoice number.",
          );
        }
        const responseData = response.data;
        let invoiceNumberValue = "";
        if (typeof responseData === "string" || typeof responseData === "number") {
          invoiceNumberValue = String(responseData);
        } else if (responseData && typeof responseData === "object") {
          if ("documentNumber" in responseData) {
            invoiceNumberValue = String(responseData.documentNumber ?? "");
          } else if ("invoiceNumber" in responseData) {
            invoiceNumberValue = String(responseData.invoiceNumber ?? "");
          } else if ("nextInvoiceNumber" in responseData) {
            invoiceNumberValue = String(responseData.nextInvoiceNumber ?? "");
          }
        }
        if (!invoiceNumberValue.trim()) {
          throw new Error("The next invoice number was not returned.");
        }
        setInvoiceNumber(invoiceNumberValue);
      })
      .catch((error: unknown) => {
        console.error("Failed to get the next invoice number:", error);
        setValidationError(
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Unable to load the next invoice number. Please try again.",
        );
      })
      .finally(() => setInvoiceNumberLoading(false));
    try {
      const response = await dispatch(
        getGoodsForInvoice({ orderId: order._id }),
      ).unwrap();
      const responseGoods = Array.isArray(response.data)
        ? response.data
        : response.data?.goods;
      const fetchedGoods = Array.isArray(responseGoods) ? responseGoods : [];
      setSelectedGoodsIds(
        fetchedGoods
          .filter(
            (good: IOrderGoods) =>
              good.invoiceGenerated !== true && Boolean(good._id),
          )
          .map((good: IOrderGoods) => good._id),
      );
    } catch (error) {
      console.error("Failed to fetch goods for invoice:", error);
      setValidationError(
        "Unable to load goods for this invoice. Please try again.",
      );
    }
  };

  const openInvoiceSummary = (ewbDetails?: EwbDtls) => {
    if (!selectedWarehouseId) {
      setValidationError("Select a warehouse before reviewing the invoice.");
      return;
    }
    setPendingEwbDetails(ewbDetails);
    setValidationError("");
    setShowEwbForm(false);
    setShowThresholdConfirmation(false);
    setShowGoodsSelection(false);
    setShowInvoiceSummary(true);
  };

  const submitGenerateInvoice = async (ewbDetails?: EwbDtls) => {
    if (!order?._id) return;
    if (selectedGoodsIds.length === 0) {
      setValidationError(
        "Select at least one item before generating the invoice.",
      );
      return;
    }
    const trimmedInvoiceNumber = invoiceNumber.trim();
    if (!trimmedInvoiceNumber) {
      setValidationError("Enter an invoice number before generating.");
      setShowGoodsSelection(true);
      return;
    }
    if (!selectedWarehouseId) {
      setValidationError("Select a warehouse before generating the invoice.");
      return;
    }
    setLoading(true);
    const previewWindow = window.open("", "_blank");
    try {
      let response: ResponseModel | void;
      if (onGenerateInvoice) {
        response = await onGenerateInvoice(
          order._id,
          trimmedInvoiceNumber,
          ewbDetails,
          selectedWarehouseId,
          billingAddress,
          shippingAddress,
          selectedGoodsIds,
        );
      } else {
        response = await dispatch(
          ewbDetails
            ? generateIRN({
                invoiceNumber: trimmedInvoiceNumber,
                orderId: order._id,
                EwbDtls: ewbDetails,
                warehouseId: selectedWarehouseId,
                billingAddress,
                shippingAddress,
                goodsIds: selectedGoodsIds,
              })
            : generateIRN({
                invoiceNumber: trimmedInvoiceNumber,
                orderId: order._id,
                warehouseId: selectedWarehouseId,
                billingAddress,
                shippingAddress,
                goodsIds: selectedGoodsIds,
              }),
        ).unwrap();
      }

      if (response?.success === false) {
        throw new Error(response.message || "Invoice generation failed.");
      }

      const invoiceId =
        typeof response?.data?.invoiceId === "string"
          ? response.data.invoiceId
          : "";
      await runInvoicePdfAction({
        dispatch,
        invoiceId,
        mode: "download",
        shippingAddress: order.shippingAddress,
        previewWindow,
      });

      onInvoiceGenerated?.();
      setShowInvoiceMenu(false);
      setShowEwbForm(false);
      setShowThresholdConfirmation(false);
      setShowInvoiceSummary(false);
      setShowGoodsSelection(false);
    } catch (error) {
      previewWindow?.close();
      console.error("Failed to generate invoice:", error);
      setValidationError(
        error instanceof Error
          ? error.message
          : "Invoice generation failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitGenerateInvoice = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!order?._id) return;

    const { TransId, TransName, VehNo, ...requiredFields } = ewbForm;
    const transporterId = TransId.trim();
    const transporterName = TransName.trim();
    const normalizedVehicleNumber = normalizeIndianVehicleNumber(VehNo);
    const hasValidVehicleNumber = normalizedVehicleNumber
      ? isValidIndianVehicleNumber(normalizedVehicleNumber)
      : Boolean(transporterId);
    const hasExactlyOneTransporter =
      Boolean(transporterId) !== Boolean(transporterName);
    const requiredValues = Object.values(requiredFields);
    const distance = Number(ewbForm.Distance);
    const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(ewbForm.TransDocDt);
    const validModes: EwbDtls["TransMode"][] = ["1", "2", "3", "4"];

    if (
      !hasExactlyOneTransporter ||
      requiredValues.some((value) => !value.trim()) ||
      !hasValidVehicleNumber ||
      !Number.isFinite(distance) ||
      distance <= 0 ||
      !isValidDate ||
      !["R", "O"].includes(ewbForm.VehType) ||
      !validModes.includes(ewbForm.TransMode as EwbDtls["TransMode"])
    ) {
      setValidationError(
        "Enter either a transporter ID or transporter name, not both; provide a valid Indian vehicle number unless a transporter ID is supplied; and complete the remaining EWB details.",
      );
      return;
    }

    const [year, month, day] = ewbForm.TransDocDt.split("-");
    const ewbDetails: EwbDtls = {
      ...(transporterId ? { TransId: transporterId } : {}),
      ...(transporterName ? { TransName: transporterName } : {}),
      Distance: distance,
      TransDocDt: `${day}/${month}/${year}`,
      ...(normalizedVehicleNumber ? { VehNo: normalizedVehicleNumber } : {}),
      VehType: ewbForm.VehType as EwbDtls["VehType"],
      TransMode: ewbForm.TransMode as EwbDtls["TransMode"],
    };

    openInvoiceSummary(ewbDetails);
  };

  const handleConfirmGenerateWithoutEwb = () => openInvoiceSummary();

  const handleContinueToInvoiceSummary = () => {
    if (selectedGoodsIds.length === 0) {
      setValidationError(
        "Select at least one item before reviewing the invoice.",
      );
      return;
    }
    if (!invoiceNumber.trim()) {
      setValidationError("Enter an invoice number before continuing.");
      return;
    }
    setValidationError("");
    setShowGoodsSelection(false);
    const selectedGoods = goodsForInvoice.filter((good) =>
      selectedGoodsIds.includes(good._id),
    );
    if (getInvoiceGoodsTotal(selectedGoods) > 50000) {
      if (warehouses.length === 0) {
        dispatch(fetchWarehouseList());
      }
      setEwbForm(
        getEwbFormFromDispatch(
          getLatestDispatchHistory(order?.dispatchHistory),
        ),
      );
      setShowThresholdConfirmation(false);
      setShowEwbForm(true);
    } else {
      setShowEwbForm(false);
      setShowThresholdConfirmation(true);
    }
  };

  const toggleSelectedGood = (goodId: string) => {
    setSelectedGoodsIds((selected) =>
      selected.includes(goodId)
        ? selected.filter((id) => id !== goodId)
        : [...selected, goodId],
    );
    setValidationError("");
  };

  const selectableGoods = goodsForInvoice.filter(
    (good) => good.invoiceGenerated !== true,
  );
  const alreadyInvoicedGoods = goodsForInvoice.filter(
    (good) => good.invoiceGenerated === true,
  );
  const selectableGoodsIds = selectableGoods
    .filter((good) => Boolean(good._id))
    .map((good) => good._id);
  const selectedGoodsTotal = getInvoiceGoodsTotal(
    goodsForInvoice.filter((good) => selectedGoodsIds.includes(good._id)),
  );
  const areAllGoodsSelected =
    selectableGoodsIds.length > 0 &&
    selectableGoodsIds.every((goodId) => selectedGoodsIds.includes(goodId));
  const toggleAllGoods = () => {
    setSelectedGoodsIds(areAllGoodsSelected ? [] : selectableGoodsIds);
    setValidationError("");
  };

  const handleBackFromInvoiceSummary = () => {
    setShowInvoiceSummary(false);
    setValidationError("");
    if (pendingEwbDetails) {
      setShowEwbForm(true);
    } else {
      setShowThresholdConfirmation(true);
    }
  };

  const handleBackToGoodsSelection = () => {
    setShowEwbForm(false);
    setShowThresholdConfirmation(false);
    setValidationError("");
    setShowGoodsSelection(true);
  };

  const handleCancelGoodsSelection = () => {
    setShowGoodsSelection(false);
    setSelectedGoodsIds([]);
    setValidationError("");
  };

  const renderInvoiceSummary = () => (
    <InvoiceSummary
      order={order}
      invoiceNumber={invoiceNumber}
      goods={goodsForInvoice.filter((good) =>
        selectedGoodsIds.includes(good._id),
      )}
      warehouseName={
        warehouseNamesAndIds.find(
          (warehouse) => warehouse._id === selectedWarehouseId,
        )?.name
      }
      billingAddress={billingAddress}
      shippingAddress={shippingAddress}
      ewbDetails={pendingEwbDetails}
    />
  );

  const renderWarehouseSelect = () => {
    const options = warehouseNamesAndIds.map((warehouse) => ({
      value: warehouse._id,
      label: warehouse.name || "",
    }));

    return (
      <div>
        <label
          htmlFor="invoice-warehouse-select"
          className="mb-1.5 block text-xs font-medium text-slate-600"
        >
          Warehouse <span className="text-rose-600">*</span>
        </label>
        <Select<{ value: string; label: string }>
          inputId="invoice-warehouse-select"
          options={options}
          value={
            options.find((option) => option.value === selectedWarehouseId) ??
            null
          }
          onChange={(option) => {
            setSelectedWarehouseId(option?.value || "");
            setValidationError("");
          }}
          isSearchable
          isClearable
          menuPosition="fixed"
          menuPortalTarget={document.body}
          placeholder="Search or select a warehouse"
          noOptionsMessage={() => "No warehouses found"}
          classNamePrefix="invoice-warehouse"
          styles={{
            control: (base, state) => ({
              ...base,
              minHeight: "42px",
              borderColor: state.isFocused ? "#10b981" : "#cbd5e1",
              boxShadow: state.isFocused ? "0 0 0 2px #d1fae5" : "none",
              "&:hover": { borderColor: "#10b981" },
            }),
            menuPortal: (base) => ({ ...base, zIndex: 120 }),
          }}
        />
      </div>
    );
  };

  const handleCancelInvoice = () => {
    setShowInvoiceMenu(false);
    setCancelInvoiceError("");
    setShowCancelConfirmation(true);
  };

  const confirmCancelInvoice = async () => {
    const invoiceId = order?.invoice?.invoiceId?.[0]?._id;
    if (!order?._id || !invoiceId) {
      setCancelInvoiceError("Unable to find the generated invoice to cancel.");
      return;
    }

    setCancelInvoiceError("");
    try {
      const response = await dispatch(
        cancelEInvoice({ invoiceId, orderId: order._id }),
      ).unwrap();
      if (response.success === false) {
        throw new Error(response.message || "Failed to cancel the invoice.");
      }

      setShowCancelConfirmation(false);
      onInvoiceGenerated?.();
    } catch (error: unknown) {
      setCancelInvoiceError(
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Failed to cancel the invoice. Please try again.",
      );
    }
  };

  const openInvoices = () => {
    const invoices = order?.invoice?.invoiceId ?? [];
    if (invoices.length > 1) {
      setShowInvoiceList(true);
      setValidationError("");
    }
  };

  const canViewInvoices =
    ["GENERATED", "PARTIAL"].includes(order?.invoice?.status || "") &&
    invoiceRecords.length > 0;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={toggleMenu}
        className={buttonClassName}
        aria-haspopup="dialog"
        aria-expanded={showInvoiceMenu}
      >
        <span>Invoice actions</span>
      </button>

      {showInvoiceMenu &&
        createPortal(
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
            onClick={(event) => {
              event.stopPropagation();
              setShowInvoiceMenu(false);
              setShowEwbForm(false);
              setShowThresholdConfirmation(false);
              setShowInvoiceSummary(false);
              setShowGoodsSelection(false);
              setShowInvoiceList(false);
              setValidationError("");
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="invoice-actions-title"
              className={`flex max-h-[90vh] w-full max-w-5xl min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl ${
                showInvoiceSummary ||
                showGoodsSelection ||
                showInvoiceList ||
                showEwbForm ||
                showThresholdConfirmation
                  ? "h-[calc(100dvh-2rem)]"
                  : ""
              }`}
              onClick={(event) => event.stopPropagation()}
            >
              <header className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                    {showThresholdConfirmation
                      ? "Confirm invoice generation"
                      : showInvoiceList
                        ? "Order invoices"
                        : showGoodsSelection
                          ? "Required goods selection"
                          : showInvoiceSummary
                            ? "Invoice review"
                            : showEwbForm
                              ? "Required EWB details"
                              : "Order documents"}
                  </p>
                  <h2
                    id="invoice-actions-title"
                    className="mt-1 text-lg font-semibold text-slate-900"
                  >
                    {showThresholdConfirmation
                      ? "Generate without EWB details?"
                      : showInvoiceList
                        ? "Select an invoice"
                        : showGoodsSelection
                          ? "Select goods for this invoice"
                          : showInvoiceSummary
                            ? "Verify invoice details"
                            : showEwbForm
                              ? "E-way bill information"
                              : "Invoice actions"}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {showThresholdConfirmation
                      ? `Selected goods total is ₹${selectedGoodsTotal.toLocaleString(
                          "en-IN",
                        )}. Continue without e-way bill details?`
                      : showInvoiceList
                        ? "Choose an invoice to open or download."
                        : showGoodsSelection
                          ? "Select at least one eligible item to include before generating the invoice."
                          : showInvoiceSummary
                            ? "Review the goods, prices, addresses, and invoice details before generating."
                            : showEwbForm
                              ? "Confirm the transporter and vehicle details to generate the invoice."
                              : "Manage the invoice and e-way bill for this order."}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close invoice actions"
                  onClick={() => {
                    setShowInvoiceMenu(false);
                    setShowEwbForm(false);
                    setShowThresholdConfirmation(false);
                    setShowInvoiceSummary(false);
                    setShowGoodsSelection(false);
                    setShowInvoiceList(false);
                    setValidationError("");
                  }}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                >
                  <X size={18} />
                </button>
              </header>

              {(ewbStatusLoading ||
                ewbStatusLoadError ||
                ewbGenerationErrors.length > 0) && (
                <div className="space-y-2 px-5 pt-4">
                  {ewbStatusLoading && (
                    <p className="text-xs text-slate-500">
                      Checking e-way bill status...
                    </p>
                  )}
                  {ewbGenerationErrors.map((error) => (
                    <div
                      key={error.invoiceId}
                      role="status"
                      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                    >
                      <AlertTriangle
                        size={17}
                        className="mt-0.5 shrink-0 text-amber-700"
                      />
                      <p>
                        <span className="font-semibold">
                          E-way bill not generated
                          {error.invoiceNumber
                            ? ` for ${error.invoiceNumber}`
                            : ""}
                          :{" "}
                        </span>
                        {error.reason}
                      </p>
                    </div>
                  ))}
                  {ewbStatusLoadError && (
                    <p role="alert" className="text-xs text-rose-700">
                      Unable to check e-way bill status for one or more
                      invoices.
                    </p>
                  )}
                </div>
              )}

              {showInvoiceList ? (
                <>
                  <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                    {invoiceRecords.map((invoice, index) => (
                      <InvoicePdfButton
                        key={invoice._id}
                        invoiceId={invoice._id || ""}
                        mode="download"
                        shippingAddress={order?.shippingAddress}
                        disabled={loading || !invoice._id}
                        onLoadingChange={setLoading}
                        onComplete={() => {
                          setShowInvoiceMenu(false);
                          setShowInvoiceList(false);
                        }}
                        className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {(isLoading) => (
                          <>
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                              {isLoading ? (
                                <LoaderCircle
                                  className="animate-spin"
                                  size={18}
                                />
                              ) : (
                                <FileText size={18} />
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-slate-900">
                                {invoice.invoiceNumber ||
                                  `Invoice ${index + 1}`}
                              </span>
                              <span className="mt-0.5 block text-xs text-slate-500">
                                {new Date(invoice.createdAt).toLocaleDateString(
                                  "en-IN",
                                )}
                              </span>
                            </span>
                          </>
                        )}
                      </InvoicePdfButton>
                    ))}
                  </div>
                  <footer className="flex justify-start border-t border-slate-100 bg-slate-50 px-5 py-3">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setShowInvoiceList(false)}
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 disabled:opacity-60"
                    >
                      Back
                    </button>
                  </footer>
                </>
              ) : showGoodsSelection ? (
                <>
                  <div className="min-h-0 flex-1 overflow-y-auto p-6">
                    {goodsLoading ? (
                      <div className="flex h-40 items-center justify-center gap-2 text-sm text-slate-500">
                        <LoaderCircle
                          size={18}
                          className="animate-spin"
                          aria-hidden="true"
                        />
                        Loading available goods...
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="max-w-xl rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 shadow-sm">
                          <label
                            htmlFor="invoice-number-input"
                            className="block text-sm font-bold text-slate-900"
                          >
                            Invoice number{" "}
                            <span className="text-rose-600">*</span>
                            <input
                              id="invoice-number-input"
                              type="text"
                              required
                              value={invoiceNumber}
                              onChange={(event) => {
                                setInvoiceNumber(event.target.value);
                                setValidationError("");
                              }}
                              disabled={invoiceNumberLoading || loading}
                              placeholder={
                                invoiceNumberLoading
                                  ? "Loading next invoice number..."
                                  : "Enter invoice number"
                              }
                              autoComplete="off"
                              className="mt-2 min-h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-4 py-3 text-base font-semibold tracking-wide text-slate-900 outline-none transition placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100 disabled:cursor-wait disabled:bg-slate-50"
                            />
                          </label>
                          <p className="mt-2 text-xs text-slate-600">
                            Edit the prefilled number if needed before
                            generating the invoice.
                          </p>
                        </div>
                        {selectableGoods.length === 0 && (
                          <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                            No selectable goods are available for this invoice.
                          </p>
                        )}
                        {selectableGoods.length > 0 && (
                          <div className="overflow-x-auto rounded-lg border border-slate-200">
                            <table className="w-full min-w-[760px] text-left text-xs">
                              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-2 py-2 text-center font-semibold">
                                    <label className="inline-flex items-center gap-1.5 normal-case tracking-normal">
                                      <input
                                        type="checkbox"
                                        aria-label={
                                          areAllGoodsSelected
                                            ? "Deselect all goods"
                                            : "Select all goods"
                                        }
                                        checked={areAllGoodsSelected}
                                        disabled={
                                          selectableGoodsIds.length === 0
                                        }
                                        onChange={toggleAllGoods}
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500 disabled:cursor-not-allowed"
                                      />
                                    </label>
                                  </th>
                                  <th className="px-3 py-2 font-semibold">
                                    Item
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    Qty
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    Unit price
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    Discount
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    GST
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    HSN
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold">
                                    Amount
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {selectableGoods.map((good: IOrderGoods) => {
                                  const goodId = good._id;
                                  const itemName =
                                    good.product?.productName ||
                                    good.rawMaterial?.name ||
                                    "Unnamed item";
                                  const quantity = Number(good.quantity) || 0;
                                  const unitPrice = Number(good.price) || 0;
                                  const discount = Number(good.discount) || 0;
                                  const gstRate =
                                    Number(good.gst || good.product?.gst) || 0;
                                  const amount =
                                    (unitPrice * quantity - discount) *
                                    (1 + gstRate / 100);
                                  return (
                                    <tr
                                      key={goodId}
                                      className={`${
                                        selectedGoodsIds.includes(goodId)
                                          ? "bg-emerald-50"
                                          : "bg-white hover:bg-slate-50"
                                      }`}
                                    >
                                      <td className="px-2 py-2 text-center">
                                        <input
                                          type="checkbox"
                                          aria-label={`Select ${itemName}`}
                                          checked={selectedGoodsIds.includes(
                                            goodId,
                                          )}
                                          disabled={!goodId}
                                          onChange={() =>
                                            toggleSelectedGood(goodId)
                                          }
                                          className="h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500 disabled:cursor-not-allowed"
                                        />
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className="font-medium text-slate-800">
                                          {itemName}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        {quantity} {good.unit || ""}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        ₹{unitPrice.toLocaleString("en-IN")}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        ₹{discount.toLocaleString("en-IN")}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        {gstRate}%
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        {good.hsn ||
                                          good.product?.hsn ||
                                          good.rawMaterial?.hsn ||
                                          "-"}
                                      </td>
                                      <td className="px-3 py-2 text-right font-medium tabular-nums">
                                        ₹{amount.toLocaleString("en-IN")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {alreadyInvoicedGoods.length > 0 && (
                          <details className="overflow-hidden rounded-lg border border-slate-200">
                            <summary className="cursor-pointer bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                              Already invoiced ({alreadyInvoicedGoods.length})
                            </summary>
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[600px] text-left text-xs">
                                <thead className="border-t border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                                  <tr>
                                    <th className="px-3 py-2 font-semibold">
                                      Item
                                    </th>
                                    <th className="px-3 py-2 text-right font-semibold">
                                      Qty
                                    </th>
                                    <th className="px-3 py-2 text-right font-semibold">
                                      Unit price
                                    </th>
                                    <th className="px-3 py-2 text-right font-semibold">
                                      GST
                                    </th>
                                    <th className="px-3 py-2 text-right font-semibold">
                                      HSN
                                    </th>
                                    <th className="px-3 py-2 text-right font-semibold">
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {alreadyInvoicedGoods.map((good) => {
                                    const quantity = Number(good.quantity) || 0;
                                    const unitPrice = Number(good.price) || 0;
                                    const discount = Number(good.discount) || 0;
                                    const gstRate =
                                      Number(good.gst || good.product?.gst) ||
                                      0;
                                    const amount =
                                      (unitPrice * quantity - discount) *
                                      (1 + gstRate / 100);
                                    return (
                                      <tr
                                        key={good._id}
                                        className="text-slate-500"
                                      >
                                        <td className="px-3 py-2">
                                          {good.product?.productName ||
                                            good.rawMaterial?.name ||
                                            "Unnamed item"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                          {quantity} {good.unit || ""}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                          ₹{unitPrice.toLocaleString("en-IN")}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                          {gstRate}%
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                          {good.hsn ||
                                            good.product?.hsn ||
                                            good.rawMaterial?.hsn ||
                                            "-"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                          ₹{amount.toLocaleString("en-IN")}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                  <footer className="shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-3">
                    {validationError && (
                      <p
                        role="alert"
                        className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                      >
                        {validationError}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <button
                        type="button"
                        disabled={loading || goodsLoading}
                        onClick={handleCancelGoodsSelection}
                        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={
                          loading ||
                          goodsLoading ||
                          invoiceNumberLoading ||
                          !invoiceNumber.trim() ||
                          selectedGoodsIds.length === 0
                        }
                        onClick={handleContinueToInvoiceSummary}
                        className="flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loading && (
                          <LoaderCircle size={16} className="animate-spin" />
                        )}
                        {selectedGoodsTotal > 50000
                          ? "Continue to EWB details"
                          : "Continue without EWB"}
                      </button>
                    </div>
                  </footer>
                </>
              ) : showInvoiceSummary ? (
                <>
                  {renderInvoiceSummary()}
                  <footer className="shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-3">
                    {validationError && (
                      <p
                        role="alert"
                        className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                      >
                        {validationError}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <button
                        type="button"
                        disabled={loading || goodsLoading}
                        onClick={handleBackFromInvoiceSummary}
                        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 disabled:opacity-60"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        disabled={loading || goodsLoading}
                        onClick={() => submitGenerateInvoice(pendingEwbDetails)}
                        className="flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loading && (
                          <LoaderCircle size={16} className="animate-spin" />
                        )}
                        {loading ? "Generating..." : "Confirm and generate"}
                      </button>
                    </div>
                  </footer>
                </>
              ) : showThresholdConfirmation ? (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="min-h-0 flex-1 overflow-y-auto p-6">
                    <div className="space-y-5">
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Selected goods total: ₹
                        {selectedGoodsTotal.toLocaleString("en-IN")}
                      </div>
                      {renderWarehouseSelect()}
                      <section className="border-t border-slate-200 pt-5">
                        <h3 className="mb-1 text-sm font-semibold text-slate-900">
                          Invoice addresses
                        </h3>
                        <p className="mb-4 text-xs text-slate-500">
                          Review the billing and delivery details for this
                          invoice.
                        </p>
                        <InvoiceAddressFields
                          billingAddress={billingAddress}
                          shippingAddress={shippingAddress}
                          setBillingAddress={setBillingAddress}
                          setShippingAddress={setShippingAddress}
                        />
                      </section>
                    </div>
                  </div>
                  <footer className="shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-3">
                    {validationError && (
                      <p
                        role="alert"
                        className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                      >
                        {validationError}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleBackToGoodsSelection}
                        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
                      >
                        Back to goods
                      </button>
                      <button
                        type="button"
                        disabled={loading || goodsLoading}
                        onClick={handleConfirmGenerateWithoutEwb}
                        className="flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loading && (
                          <LoaderCircle size={16} className="animate-spin" />
                        )}
                        Continue to invoice review
                      </button>
                    </div>
                  </footer>
                </div>
              ) : showEwbForm ? (
                <form
                  onSubmit={handleSubmitGenerateInvoice}
                  className="flex min-h-0 flex-col"
                >
                  <div className="min-h-0 space-y-6 overflow-y-auto px-6 py-5">
                    {renderWarehouseSelect()}
                    <section className="border-t border-slate-200 pt-5">
                      <h3 className="mb-4 text-sm font-semibold text-slate-900">
                        E-way bill details
                      </h3>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {(
                          [
                            ["TransId", "Transporter ID", "text"],
                            ["TransName", "Transporter name", "text"],
                            ["Distance", "Distance (km)", "number"],
                            ["TransDocDt", "Transport document date", "date"],
                            [
                              "VehNo",
                              "Vehicle number (required unless transporter ID is provided)",
                              "text",
                            ],
                          ] as const
                        ).map(([field, label, inputType]) => (
                          <label
                            key={field}
                            className="block text-xs font-medium text-slate-600"
                          >
                            {label}
                            <input
                              type={inputType}
                              min={inputType === "number" ? "1" : undefined}
                              step={inputType === "number" ? "any" : undefined}
                              disabled={field === "Distance" && distanceLoading}
                              value={ewbForm[field]}
                              onChange={(event) =>
                                updateEwbField(field, event.target.value)
                              }
                              className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                            />
                            {field === "VehNo" && (
                              <span className="mt-1 block text-[11px] font-normal text-slate-500">
                                Optional when a transporter ID is provided.
                                Spaces and hyphens are accepted.
                              </span>
                            )}
                          </label>
                        ))}
                        <label className="block text-xs font-medium text-slate-600">
                          Vehicle type
                          <select
                            value={ewbForm.VehType}
                            onChange={(event) =>
                              updateEwbField("VehType", event.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                          >
                            <option value="">Select vehicle type</option>
                            <option value="R">Regular</option>
                            <option value="O">Over dimensional cargo</option>
                          </select>
                        </label>
                        <label className="block text-xs font-medium text-slate-600">
                          Transport mode
                          <select
                            value={ewbForm.TransMode}
                            onChange={(event) =>
                              updateEwbField("TransMode", event.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                          >
                            <option value="">Select transport mode</option>
                            <option value="1">Road</option>
                            <option value="2">Rail</option>
                            <option value="3">Air</option>
                            <option value="4">Ship</option>
                          </select>
                        </label>
                      </div>
                    </section>
                    <section className="border-t border-slate-200 pt-5">
                      <h3 className="mb-1 text-sm font-semibold text-slate-900">
                        Invoice addresses
                      </h3>
                      <p className="mb-4 text-xs text-slate-500">
                        Review the billing and delivery details for this
                        invoice.
                      </p>
                      <InvoiceAddressFields
                        billingAddress={billingAddress}
                        shippingAddress={shippingAddress}
                        setBillingAddress={setBillingAddress}
                        setShippingAddress={setShippingAddress}
                      />
                    </section>
                  </div>

                  <footer className="shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-3">
                    {validationError && (
                      <p
                        role="alert"
                        className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                      >
                        {validationError}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleBackToGoodsSelection}
                        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 disabled:opacity-60"
                      >
                        Back to goods
                      </button>
                      <button
                        type="submit"
                        disabled={loading || goodsLoading}
                        className="flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {loading && (
                          <LoaderCircle size={16} className="animate-spin" />
                        )}
                        Continue to invoice review
                      </button>
                    </div>
                  </footer>
                </form>
              ) : (
                <>
                  <div className="space-y-2 p-4">
                    {type === "order" &&
                      order?.invoice?.status !== "GENERATED" && (
                        <button
                          type="button"
                          disabled={loading}
                          onClick={handleGenerateInvoice}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                            <FileText size={19} />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-slate-900">
                              Generate invoice
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              Create the tax invoice and IRN.
                            </span>
                          </span>
                        </button>
                      )}

                    {type === "order" &&
                      canViewInvoices &&
                      (invoiceRecords.length === 1 ? (
                        <InvoicePdfButton
                          invoiceId={invoiceRecords[0]._id || ""}
                          mode="download"
                          shippingAddress={order?.shippingAddress}
                          disabled={loading}
                          onLoadingChange={setLoading}
                          onComplete={() => setShowInvoiceMenu(false)}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {(isLoading) => (
                            <>
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                                {isLoading ? (
                                  <LoaderCircle
                                    className="animate-spin"
                                    size={19}
                                  />
                                ) : (
                                  <FileText size={19} />
                                )}
                              </span>
                              <span>
                                <span className="block text-sm font-semibold text-slate-900">
                                  View invoice
                                </span>
                                <span className="mt-0.5 block text-xs text-slate-500">
                                  Open or download the generated PDF.
                                </span>
                              </span>
                            </>
                          )}
                        </InvoicePdfButton>
                      ) : (
                        <button
                          type="button"
                          disabled={loading}
                          onClick={openInvoices}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                            <FileText size={19} />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-slate-900">
                              View invoices
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              Choose from {invoiceRecords.length} generated
                              invoices.
                            </span>
                          </span>
                        </button>
                      ))}

                    {type === "order" &&
                      order?.invoice?.status === "GENERATED" && (
                        <button
                          type="button"
                          disabled={loading}
                          onClick={handleCancelInvoice}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                            <Ban size={19} />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-slate-900">
                              Cancel invoice
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              Cancel the currently generated invoice.
                            </span>
                          </span>
                        </button>
                      )}

                  </div>

                  <footer className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInvoiceMenu(false);
                        setShowEwbForm(false);
                        setValidationError("");
                      }}
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                    >
                      Close
                    </button>
                  </footer>
                </>
              )}
            </section>
          </div>,
          document.body,
        )}
      {createPortal(
        <ConfirmationModal
          isOpen={showCancelConfirmation}
          onClose={() => {
            if (!cancelInvoiceLoading) {
              setShowCancelConfirmation(false);
              setCancelInvoiceError("");
            }
          }}
          onConfirm={confirmCancelInvoice}
          title="Cancel this invoice?"
          message={
            cancelInvoiceError ||
            "This will cancel the generated e-invoice. This action cannot be undone."
          }
          confirmText="Cancel invoice"
          cancelText="Keep invoice"
          type="danger"
          isLoading={cancelInvoiceLoading}
        />,
        document.body,
      )}
    </div>
  );
};

export default InvoiceActions;
